import type { GridColumnResizeEvent } from "@tql/ui-components-react/kendo/grid";
import { useMemo } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import { getColumnConfig } from "../components/commodities-grid/commodities-grid-columns.tsx";
import type {
	CommoditiesColumnsConfig,
	CommoditiesColumnsData,
	CommoditiesColumnsHandlers,
	CommoditiesColumnsState,
} from "./commodities-columns-config.ts";

const TRAILING_FIXED_DATA_FIELDS = ["isDefault"] as const;

type UseCommoditiesColumnsArgs = {
	state: CommoditiesColumnsState;
	data: CommoditiesColumnsData;
	handlers: CommoditiesColumnsHandlers;
	visibleColumns?: BoardColumn[];
	onColumnResize?: (event: GridColumnResizeEvent) => void;
};

export const useCommoditiesColumns = ({
	state,
	data,
	handlers,
	visibleColumns,
	onColumnResize,
}: UseCommoditiesColumnsArgs): BoardColumn[] => {
	const columnConfig = useMemo(
		() =>
			getColumnConfig({
				state,
				data,
				handlers,
			} satisfies CommoditiesColumnsConfig),
		[state, data, handlers],
	);

	return useMemo(() => {
		// Base behavior: if no user preference payload is supplied, render the feature's default column config.
		if (!visibleColumns) return columnConfig;

		// Resize support is used as the signal that we are in the "preferences-enabled" mode,
		// where width/order settings from user preferences should be applied.
		const shouldApplyPreferenceOverlay = Boolean(onColumnResize);

		// "Select" and "Actions" are utility columns that stay pinned at the edges.
		// Only data columns participate in preference-driven show/hide/order/width behavior.
		const selectColumns = columnConfig.filter((col) => col.title === "Select");
		const actionColumns = columnConfig.filter((col) => col.title === "Actions");
		const dataColumns = columnConfig.filter(
			(col) => col.title !== "Actions" && col.title !== "Select",
		);
		const trailingFixedDataColumns = TRAILING_FIXED_DATA_FIELDS.flatMap(
			(field) => dataColumns.filter((col) => col.field === field),
		);
		const reorderableDataColumns = dataColumns.filter(
			(col) => !TRAILING_FIXED_DATA_FIELDS.includes(col.field as "isDefault"),
		);

		// Build quick lookup of saved column preference objects by field name.
		// These preference entries can include show/orderIndex/width values.
		const visibleColumnByField = new Map(
			visibleColumns
				.filter((col) => typeof col.field === "string" && col.field.length > 0)
				.map((col) => [String(col.field), col]),
		);

		if (!shouldApplyPreferenceOverlay) {
			// Lightweight mode: apply only visibility flags from preferences.
			// We intentionally keep default ordering and sizing from the base config.
			const visibleDataColumns = reorderableDataColumns.filter((col) => {
				const field = typeof col.field === "string" ? col.field : "";
				if (!field) return true;
				const selectedColumn = visibleColumnByField.get(field);
				return selectedColumn ? selectedColumn.show !== false : true;
			});
			const visibleTrailingFixedDataColumns = trailingFixedDataColumns
				.filter((col) => {
					const field = typeof col.field === "string" ? col.field : "";
					if (!field) return true;
					const selectedColumn = visibleColumnByField.get(field);
					return selectedColumn ? selectedColumn.show !== false : true;
				})
				.map((col) => ({ ...col, reorderable: false }));

			return [
				...selectColumns,
				...visibleDataColumns,
				...visibleTrailingFixedDataColumns,
				...actionColumns,
			];
		}

		// Full preference mode: rebuild the data-column sequence from saved preferences.
		// Only visible columns are kept, then they are sorted by persisted orderIndex.
		const orderedVisibleDataColumns = [...visibleColumns]
			.filter(
				(col) =>
					typeof col.field === "string" &&
					col.field.length > 0 &&
					!TRAILING_FIXED_DATA_FIELDS.includes(col.field as "isDefault") &&
					col.show !== false,
			)
			.sort(
				(a, b) =>
					(a.orderIndex ?? Number.MAX_SAFE_INTEGER) -
					(b.orderIndex ?? Number.MAX_SAFE_INTEGER),
			)
			.map((selectedColumn) => {
				// Map each saved preference back to its runtime column definition.
				// If the backend returns a stale/unknown field, we drop it safely.
				const baseColumn = reorderableDataColumns.find(
					(col) => col.field === selectedColumn.field,
				);
				if (!baseColumn) {
					return null;
				}

				// Persisted width wins when present; otherwise preserve default width.
				return typeof selectedColumn.width === "number"
					? { ...baseColumn, width: selectedColumn.width }
					: baseColumn;
			})
			.filter((col): col is BoardColumn => col !== null);

		// Track fields already emitted so we can append any non-persisted defaults once.
		const includedFields = new Set(
			orderedVisibleDataColumns
				.map((col) => col.field)
				.filter((field): field is string => typeof field === "string"),
		);

		// Fallback: include default columns not present in saved preferences (for new releases),
		// while still honoring explicit show=false when a preference exists.
		const fallbackVisibleColumns = reorderableDataColumns.filter((col) => {
			const field = typeof col.field === "string" ? col.field : "";
			if (!field || includedFields.has(field)) {
				return false;
			}
			const selectedColumn = visibleColumnByField.get(field);
			return selectedColumn ? selectedColumn.show !== false : true;
		});

		const visibleTrailingFixedDataColumns = trailingFixedDataColumns
			.filter((col) => {
				const field = typeof col.field === "string" ? col.field : "";
				if (!field) return true;
				const selectedColumn = visibleColumnByField.get(field);
				return selectedColumn ? selectedColumn.show !== false : true;
			})
			.map((col) => {
				const field = typeof col.field === "string" ? col.field : "";
				const selectedColumn = visibleColumnByField.get(field);
				return typeof selectedColumn?.width === "number"
					? { ...col, width: selectedColumn.width, reorderable: false }
					: { ...col, reorderable: false };
			});

		return [
			...selectColumns,
			...orderedVisibleDataColumns,
			...fallbackVisibleColumns,
			...visibleTrailingFixedDataColumns,
			...actionColumns,
		];
	}, [columnConfig, onColumnResize, visibleColumns]);
};
